import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  ArrowDownUp,
  ArrowUpDown,
  Plus,
  ShieldOff,
  ShieldX,
  SquareCheck,
  SquarePen,
  SquareX,
  RefreshCw,
  CheckSquare,
  ArrowRight,
  FileCheck2,
  Hourglass,
} from "lucide-react";
import { ShowError, ShowSuccess } from "../../../utils/ToasFunction";
import ParagraphTag from "../../reusebale/Paragraph";
import ImageTag from "../../reusebale/imageTag";
import NoData from "../../reusebale/noData";
import LeaseTableLoader from "../../skeleton-loader/lease-table";

import * as LeaseAction from "../../../store/actions/lease/LeaseAction";

import BaseUrl from "../../../constants/BaseUrl";
import db from "../../../db/index.json";
import ReactPaginate from "react-paginate";

const LeasesTable = ({ isViewDetails, onViewCreate, search, status }) => {
  const { lease } = db;
  const { loginSuccess } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [completingLeaseId, setCompletingLeaseId] = useState(null);
  const dispatch = useDispatch();

  const [AllLeases, setAllLeases] = useState([]);
  const [filteredLeases, setFilteredLeases] = useState([]);

  // Check if lease has expired or is within 7 days of expiring
  const isLeaseExpired = (endDate) => {
    const today = moment();
    const sevenDaysBeforeExpiry = moment(endDate).subtract(7, "days");
    return today.isAfter(sevenDaysBeforeExpiry, "day");
  };

  // Handle Lease Renewal
  const handleLeaseRenewal = (leaseData) => {
    console.log("leaseRewal Triggered");
    // Set renewal mode and populate form with existing lease data
    dispatch(LeaseAction.SetLeaseRenewalModeAction(true));
    dispatch(LeaseAction.LeaseDetailAction(leaseData));
    dispatch(LeaseAction.ChangePropertyCreateSteps(1));
    onViewCreate(); // Navigate to create form
  };

  // Handle Mark Complete
  const handleMarkComplete = (leaseId) => {
    console.log("Mark Complete clicked for lease:", leaseId);

    // Prevent multiple clicks
    if (completingLeaseId === leaseId) {
      return;
    }

    // Find the lease data
    const leaseData = AllLeases.find((lease) => lease._id === leaseId);
    if (!leaseData) {
      ShowError("Lease data not found");
      return;
    }

    setCompletingLeaseId(leaseId);

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", `Bearer ${loginSuccess?.token}`);

    const raw = JSON.stringify({
      leaseId: leaseId,
      status: "completed",
      isComplete: true,
    });

    const requestOptions = {
      method: "PUT",
      headers: myHeaders,
      body: raw,
      redirect: "follow",
    };

    fetch(`${BaseUrl}api/lease/completeLease`, requestOptions)
      .then((response) => response.json())
      .then((result) => {
        if (result.status === "success") {
          ShowSuccess("Lease marked as completed successfully");
          // Refresh the lease list
          GetAllLease();
        } else {
          ShowError(result?.message || "Failed to complete lease");
        }
      })
      .catch((error) => {
        console.error("Error completing lease:", error);
        ShowError("Failed to complete lease");
      })
      .finally(() => {
        setCompletingLeaseId(null);
      });
  };

  function CalculateTotalRent(item) {
    if (!item) {
      return "$ 0";
    }

    // Get base rent from unitInfo
    const baseRent = Number(item.unitInfo?.rent) || 0;

    // Calculate additional charges
    const additionalChargesTotal = Array.isArray(item.additionalCharges)
      ? item.additionalCharges.reduce((sum, charge) => {
          return sum + (Number(charge.amount) || 0);
        }, 0)
      : 0;

    // Total rent = base rent + additional charges
    const total = baseRent + additionalChargesTotal;

    return `$ ${total}`;
  }

  function CalculateDaysBetween(date1, date2) {
    const startDate = moment(date1);
    const endDate = moment(date2);
    return endDate.diff(startDate, "days");
  }

  const GetAllLease = () => {
    setAllLeases([]);
    setLoading(true);
    const myHeaders = new Headers();
    myHeaders.append("Authorization", `Bearer ${loginSuccess?.token}`);
    const requestOptions = {
      method: "GET",
      headers: myHeaders,
      redirect: "follow",
    };

    fetch(
      `${BaseUrl}api/lease/getAllLeaseByLanlordId?status=${status}`,
      requestOptions
    )
      .then((response) => response.json())
      .then((result) => {
        if (result.success) {
          const data = result.data.map((item) => ({
            ...item,
            duration: CalculateDaysBetween(item?.startDate, item?.endDate),
            rent: CalculateTotalRent(item),
          }));
          setAllLeases(data);
          setLoading(false);
        } else {
          ShowError(result.message);
          setLoading(false);
          setAllLeases([]);
        }
      })
      .catch((error) => console.error(error));
  };

  useEffect(() => {
    GetAllLease();
  }, [status]);

  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;

  useEffect(() => {
    if (!search) {
      setFilteredLeases(AllLeases);
    } else {
      const filtered = AllLeases.filter((item) => {
        const tenantName = `${item?.tenantInfo?.firstName} ${item?.tenantInfo?.lastName}`;
        return (
          item?.propertyInfo?.propertyName
            ?.toUpperCase()
            .includes(search.toUpperCase()) ||
          item?.unitInfo?.unitName
            ?.toUpperCase()
            .includes(search.toUpperCase()) ||
          tenantName?.toUpperCase().includes(search.toUpperCase())
        );
      });
      setFilteredLeases(filtered);
      setCurrentPage(0);
    }
  }, [search, AllLeases]);

  const paginatedData = filteredLeases.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  const handlePageClick = ({ selected }) => {
    setCurrentPage(selected);
  };

  const onViewDetails = (status, id) => {
    isViewDetails(status, id);
  };

  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });

    const sorted = [...filteredLeases].sort((a, b) => {
      if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
      if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setFilteredLeases(sorted);
  };

  return (
    <div className="h-full">
      <div className="bg-white w-full h-[87%]">
        <div className="w-[95%] mx-auto h-[98%]">
          <div className="flex justify-between items-center h-[12%]">
            <div className="w-[70%]">
              <ParagraphTag
                content={lease.heading}
                classes="text-darkColor font-popinsSemiBold text-lg"
              />
            </div>

            <div className="w-[30%] flex justify-end">
              <button
                className="w-[48%] bg-themeColor rounded-md flex items-center justify-center py-2"
                onClick={() => {
                  dispatch(LeaseAction.ClearAllLeaseDetailsAction());
                  dispatch(LeaseAction.ClearLeaseRenewalModeAction()); // Clear renewal mode for new lease
                  dispatch(LeaseAction.ChangePropertyCreateSteps(1));

                  onViewCreate();
                }}
              >
                <Plus color="#FFFFFF" size={15} />
                <ParagraphTag
                  content={lease.new}
                  classes="text-white font-popinsSemiBold text-xs pl-2"
                />
              </button>
            </div>
          </div>
          <div className="overflow-x-auto w-full h-[88%] panelScroll mt-1">
            <table className="w-full ">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-black/20">
                  <th className="w-[20%] text-left pb-2">
                    <span className="flex">
                      <ParagraphTag
                        content={lease.heading}
                        classes="text-darkColor/40 font-popinsSemiBold text-xs pr-1"
                      />
                    </span>
                  </th>
                  <th className="w-[8%] pb-2">
                    <span className="flex justify-center">
                      <ParagraphTag
                        content={lease.units}
                        classes="text-darkColor/40 font-popinsSemiBold text-xs pr-1"
                      />
                    </span>
                  </th>
                  <th className="w-[8%] pb-2">
                    <span className="flex justify-center">
                      <ParagraphTag
                        content={lease.status}
                        classes="text-darkColor/40 font-popinsSemiBold text-xs pr-1"
                      />
                      {sortConfig.key === "status" ? (
                        sortConfig.direction === "asc" ? (
                          <ArrowUpDown
                            className="cursor-pointer"
                            size={13}
                            color="#6F6F6F"
                            onClick={() => requestSort("status")}
                          />
                        ) : (
                          <ArrowDownUp
                            className="cursor-pointer"
                            size={13}
                            color="#6F6F6F"
                            onClick={() => requestSort("status")}
                          />
                        )
                      ) : (
                        <ArrowUpDown
                          className="cursor-pointer"
                          size={13}
                          color="#6F6F6F"
                          onClick={() => requestSort("status")}
                        />
                      )}
                    </span>
                  </th>

                  <th className="w-[20%] pb-2">
                    <span className="flex justify-center">
                      <ParagraphTag
                        content={lease.duration}
                        classes="text-darkColor/40 font-popinsSemiBold text-xs pl-2"
                      />
                      {sortConfig.key === "duration" ? (
                        sortConfig.direction === "asc" ? (
                          <ArrowUpDown
                            className="cursor-pointer"
                            size={13}
                            color="#6F6F6F"
                            onClick={() => requestSort("duration")}
                          />
                        ) : (
                          <ArrowDownUp
                            className="cursor-pointer"
                            size={13}
                            color="#6F6F6F"
                            onClick={() => requestSort("duration")}
                          />
                        )
                      ) : (
                        <ArrowUpDown
                          className="cursor-pointer"
                          size={13}
                          color="#6F6F6F"
                          onClick={() => requestSort("duration")}
                        />
                      )}
                    </span>
                  </th>

                  <th className="w-[8%] pb-2">
                    <span className="flex justify-center">
                      <ParagraphTag
                        content={lease.rent}
                        classes="text-darkColor/40 font-popinsSemiBold text-xs pr-1"
                      />
                      {sortConfig.key === "rent" ? (
                        sortConfig.direction === "asc" ? (
                          <ArrowUpDown
                            className="cursor-pointer"
                            size={13}
                            color="#6F6F6F"
                            onClick={() => requestSort("rent")}
                          />
                        ) : (
                          <ArrowDownUp
                            className="cursor-pointer"
                            size={13}
                            color="#6F6F6F"
                            onClick={() => requestSort("rent")}
                          />
                        )
                      ) : (
                        <ArrowUpDown
                          className="cursor-pointer"
                          size={13}
                          color="#6F6F6F"
                          onClick={() => requestSort("rent")}
                        />
                      )}
                    </span>
                  </th>
                  <th className="w-[8%] pb-2">
                    <span className="flex justify-center">
                      <ParagraphTag
                        content={lease.deposit}
                        classes="text-darkColor/40 font-popinsSemiBold text-xs pr-1"
                      />
                      {sortConfig.key === "deposit" ? (
                        sortConfig.direction === "asc" ? (
                          <ArrowUpDown
                            className="cursor-pointer"
                            size={13}
                            color="#6F6F6F"
                            onClick={() => requestSort("securityDepositAmount")}
                          />
                        ) : (
                          <ArrowDownUp
                            className="cursor-pointer"
                            size={13}
                            color="#6F6F6F"
                            onClick={() => requestSort("securityDepositAmount")}
                          />
                        )
                      ) : (
                        <ArrowUpDown
                          className="cursor-pointer"
                          size={13}
                          color="#6F6F6F"
                          onClick={() => requestSort("securityDepositAmount")}
                        />
                      )}
                    </span>
                  </th>

                  <th className="w-[18%] text pr-4 pb-2">
                    <ParagraphTag
                      content={lease.actions}
                      classes="text-darkColor/40 font-popinsSemiBold text-xs"
                    />
                  </th>
                  <th className="w-[10%] text-right pr-4">
                    <ParagraphTag
                      content={"Quick Actions"}
                      classes="text-darkColor/40 font-popinsSemiBold text-xs"
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((item) => {
                  const TenantName = `${item?.tenantInfo.firstName} ${item?.tenantInfo.lastName}`;
                  if (
                    item?.propertyInfo?.propertyName
                      ?.toUpperCase()
                      ?.includes(search.toUpperCase()) ||
                    item.unitInfo.unitName
                      ?.toUpperCase()
                      ?.includes(search.toUpperCase()) ||
                    TenantName?.trim()
                      ?.toUpperCase()
                      ?.includes(search.toUpperCase())
                  ) {
                    return (
                      <tr
                        key={item._id}
                        className="hover:bg-gray-100 rounded-md cursor-pointer"
                      >
                        <td className="py-2">
                          <div className="flex items-center justify-between">
                            <div className="w-[18%] rounded-full">
                              <ImageTag
                                path="/assets/daashboard/balance.png"
                                classes="w-full"
                                altText="logo"
                              />
                            </div>

                            <div
                              className="w-[78%] cursor-pointer"
                              onClick={() => {
                                onViewDetails(item.status, item._id);
                              }}
                            >
                              <ParagraphTag
                                content={item.ID}
                                classes="text-darkColor/50 font-popinsMedium text-xs"
                              />
                              <ParagraphTag
                                content={item?.propertyInfo?.propertyName}
                                classes="text-darkColor font-popinsSemiBold text-sm"
                              />
                            </div>
                          </div>
                        </td>
                        <td className="py-2 px-4 font-popinsMedium text-darkColor text-sm text-center">
                          {item?.unitInfo?.unitName}
                        </td>
                        <td className="py-2 font-popinsMedium text-darkColor text-sm">
                          <span className="flex justify-center">
                            {(() => {
                              switch (item.status) {
                                case "active":
                                  return (
                                    <SquareCheck color="#01CB78" size={20} />
                                  );
                                case "draft":
                                  return (
                                    <SquarePen color="#e38979" size={20} />
                                  );
                                case "rejected":
                                  return <SquareX color="#E43B3B" size={20} />;
                                case "inActive":
                                  return (
                                    <ShieldOff color="#BFCDDB" size={20} />
                                  );
                                case "completed":
                                  return (
                                    <FileCheck2 color="#01CB78" size={20} />
                                  );
                                case "pendingPayment":
                                  return (
                                    <Hourglass color="#F59E0B" size={20} />
                                  );
                                default:
                                  return <ShieldX color="#FF0000" size={20} />;
                              }
                            })()}
                          </span>
                        </td>

                        <td className="py-2 px-4 font-popinsMedium text-darkColor text-xs text-center">
                          <div className="flex flex-col">
                            <span>{`${moment(item?.startDate).format(
                              "MM/DD/YYYY"
                            )} - ${moment(item?.endDate).format(
                              "MM/DD/YYYY"
                            )}`}</span>
                            <span className="text-darkColor/50">{`${CalculateDaysBetween(
                              item?.startDate,
                              item?.endDate
                            )} Days`}</span>
                          </div>
                        </td>
                        <td className="py-2 px-4 font-popinsMedium text-darkColor text-sm text-center">
                          {item?.rent}
                        </td>
                        <td className="py-2 px-4 font-popinsMedium text-darkColor text-sm text-center">
                          {`$ ${
                            item?.securityDepositAmount
                              ? item?.securityDepositAmount
                              : 0
                          }`}
                        </td>
                        <td className="py-2 px-4 font-popinsMedium text-darkColor text-sm">
                          <div className="flex flex-col text-center">
                            <span className="text-xs">{`${item?.tenantInfo.firstName} ${item?.tenantInfo.lastName}`}</span>
                            <span className="text-xs text-darkColor/50">{`${item?.tenantInfo.cellNo}`}</span>
                          </div>
                        </td>
                        <td className="py-2 px-4 font-popinsMedium text-darkColor text-sm">
                          <div className="flex justify-between items-end gap-2">
                            {/* Action buttons - show renewal buttons if lease has expired */}
                            <div className="flex items-end gap-1 ml-5">
                              {isLeaseExpired(item?.endDate) &&
                              item?.status === "active" ? (
                                <>
                                  <button
                                    onClick={() => handleMarkComplete(item._id)}
                                    disabled={completingLeaseId === item._id}
                                    className={`px-2 py-1 rounded text-xs transition-colors ${
                                      completingLeaseId === item._id
                                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                        : "bg-green-100 text-green-700 hover:bg-green-200"
                                    }`}
                                    title="Mark Complete"
                                  >
                                    {completingLeaseId === item._id ? (
                                      <div className="animate-spin">
                                        <RefreshCw size={14} />
                                      </div>
                                    ) : (
                                      <CheckSquare size={14} />
                                    )}
                                  </button>
                                  <button
                                    onClick={() => handleLeaseRenewal(item)}
                                    className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200 transition-colors flex items-center gap-1"
                                    title="Renew Lease"
                                  >
                                    <RefreshCw size={14} />
                                    <span>Renew</span>
                                  </button>
                                </>
                              ) : (
                                <div
                                  className="flex justify-center items-center border border-themeColor w-7 h-7 rounded-md cursor-pointer"
                                  onClick={() =>
                                    onViewDetails(item.status, item._id)
                                  }
                                >
                                  <ArrowRight
                                    color="#6F6F6F"
                                    size={18}
                                    strokeWidth={2}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  }
                  return null; // Return null for items that don't match search criteria
                })}
              </tbody>
            </table>
            {loading && (
              // <div className="flex justify-center items-center w-full mt-20">
              <LeaseTableLoader />
              // </div>
            )}
            {AllLeases.length === 0 && !loading && (
              <div className="w-full flex justify-center mt-5">
                <NoData />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex justify-center h-[13%] items-center">
        <ReactPaginate
          previousLabel="Previous"
          nextLabel="Next"
          breakLabel="..."
          pageCount={Math.ceil(paginatedData.length / itemsPerPage)}
          marginPagesDisplayed={2}
          pageRangeDisplayed={3}
          onPageChange={handlePageClick}
          containerClassName="flex space-x-2 items-center"
          pageClassName="px-3 py-1 border rounded-md text-sm font-popinsMedium cursor-pointer"
          previousClassName="px-3 py-1 border rounded-l-md text-sm cursor-pointer font-popinsMedium"
          nextClassName="px-3 py-1 border rounded-r-md text-sm font-popinsMedium cursor-pointer font-popinsMedium"
          disabledClassName="opacity-50 !cursor-not-allowed"
          activeClassName="bg-themeColor text-white"
          breakClassName="px-3 py-1 text-sm font-popinsMedium"
        />
      </div>
    </div>
  );
};

export default LeasesTable;
